import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { INTEGRATIONS, type IntegrationDefinition } from '@/config/integrations';

interface IntegrationsState {
  /**
   * Verified connections only. Unconditionally empty until real, server-side
   * connection records exist. Secret presence and label rows never count.
   */
  connected: Set<string>;
  /**
   * Legacy `user_integrations` label rows for the signed-in user. Unverified
   * metadata only — NEVER a provider connection. Kept so the owner can see
   * and remove them; no new label rows can be created from the app.
   */
  personal: Set<string>;
  /** Platform-configured capability availability — NOT a connection. */
  platformAvailable: Set<string>;
  loading: boolean;
  /** True until the first status request settles (success or failure). */
  initialLoading: boolean;
  /** Non-secret, user-safe error message for the last failed refresh. */
  error: string | null;
  refresh: () => Promise<boolean>;
  /** Removes the user's own unverified legacy label row (own-row RLS). */
  disconnectPersonal: (provider: string) => Promise<void>;
}

const PLATFORM_IDS = new Set(INTEGRATIONS.filter((i) => i.platformManaged).map((i) => i.id));

/**
 * Parses the integrations-status response. Only platform-managed ids are read,
 * from `platform` (current contract) or the legacy `connected` map. Any other
 * id in the response is ignored: a server secret is platform configuration,
 * not a user's or workspace's connection.
 */
export function parsePlatformStatus(data: unknown): Set<string> {
  const d = (data ?? {}) as { platform?: Record<string, unknown>; connected?: Record<string, unknown> };
  const map = (d.platform ?? d.connected ?? {}) as Record<string, unknown>;
  const out = new Set<string>();
  for (const id of PLATFORM_IDS) if (map[id] === true) out.add(id);
  return out;
}

export function useIntegrations(): IntegrationsState {
  const [platformAvailable, setPlatformAvailable] = useState<Set<string>>(new Set());
  const [personal, setPersonal] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id: a response only applies if it is still the latest
  // request AND the signed-in user has not changed since it was issued.
  const seqRef = useRef(0);
  const userRef = useRef<string | null | undefined>(undefined);
  const mountedRef = useRef(true);

  const clearAll = useCallback(() => {
    setPlatformAvailable(new Set());
    setPersonal(new Set());
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    const seq = ++seqRef.current;
    setLoading(true);
    let uid: string | null = null;
    try {
      const { data: userRes } = await supabase.auth.getUser();
      uid = userRes?.user?.id ?? null;
      const stale = () => !mountedRef.current || seq !== seqRef.current;
      if (stale()) return false;
      if (!uid) {
        clearAll();
        setError(null);
        return false;
      }
      userRef.current = uid;
      const [statusRes, personalRes] = await Promise.all([
        supabase.functions.invoke('integrations-status'),
        supabase.from('user_integrations').select('provider'),
      ]);
      if (stale() || userRef.current !== uid) return false;
      if (statusRes.error) throw new Error('status');
      // Legacy/new function bodies may carry an error field with a 2xx status.
      if ((statusRes.data as { error?: unknown } | null)?.error) throw new Error('status');
      if (personalRes.error) throw new Error('labels');
      setPlatformAvailable(parsePlatformStatus(statusRes.data));
      const rows = (personalRes.data ?? []) as Array<{ provider: string }>;
      setPersonal(new Set(rows.map((r) => r.provider)));
      setError(null);
      return true;
    } catch {
      if (!mountedRef.current || seq !== seqRef.current) return false;
      // Clear stale status so the UI cannot keep asserting unconfirmed state.
      clearAll();
      setError("Couldn't load integration status. Check your connection and try again.");
      return false;
    } finally {
      if (mountedRef.current && seq === seqRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [clearAll]);

  const disconnectPersonal: IntegrationsState['disconnectPersonal'] = async (provider) => {
    const { data: userRes } = await supabase.auth.getUser();
    const uid = userRes?.user?.id;
    if (!uid) return;
    const { error: delErr } = await supabase
      .from('user_integrations')
      .delete()
      .eq('user_id', uid)
      .eq('provider', provider);
    if (delErr) throw new Error("Couldn't remove that label. Try again.");
    if (userRef.current !== uid) return;
    setPersonal((s) => {
      const next = new Set(s);
      next.delete(provider);
      return next;
    });
  };

  const deferredRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // Never call Supabase auth APIs synchronously inside this callback
      // (auth-lock deadlock risk). Only synchronous state updates here; any
      // refresh is deferred to a tracked timer.
      const nextUid = session?.user?.id ?? null;
      if (event === 'SIGNED_OUT' || nextUid !== userRef.current) {
        seqRef.current++; // invalidate in-flight requests
        userRef.current = nextUid;
        clearAll();
        if (deferredRef.current) clearTimeout(deferredRef.current);
        deferredRef.current = null;
        if (!nextUid) {
          setLoading(false);
          setInitialLoading(false);
          setError(null);
          return;
        }
        deferredRef.current = setTimeout(() => {
          deferredRef.current = null;
          if (mountedRef.current) void refresh();
        }, 0);
      }
    });
    return () => {
      mountedRef.current = false;
      seqRef.current++;
      if (deferredRef.current) clearTimeout(deferredRef.current);
      data?.subscription?.unsubscribe();
    };
  }, [refresh, clearAll]);

  // Fresh empty set per render: no shared mutable singleton.
  const connected = new Set<string>();

  return {
    connected,
    personal,
    platformAvailable,
    loading,
    initialLoading,
    error,
    refresh,
    disconnectPersonal,
  };
}

export type { IntegrationDefinition };
